import { Box, Typography } from "@mui/material";
import { DataTable } from "@zfp-akademie-a-s/zfp-design";
import { calculateFinalOneTimeRepaymentAmount } from "Charts/Utils/PropertyPurchaseChartUtils";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import type { ReactNode } from "react";

type Props = {
  readonly originalLoanDuration: number;
  readonly loanPaymentAmount: number;
  readonly loanInterestRate: number;
  readonly loanAmount: number;
};

type Variant = {
  readonly variant: string;
  readonly loanDuration: number;
  readonly loanTotalAmount: number;
  readonly savings: number;
  readonly oneTimeAmount: number;
};

const HeaderCell = ({ children }: { readonly children: ReactNode }) => (
  <Box p={Spacing.XS}>
    <Typography lineHeight={1.3} variant="caption" fontWeight={500} color="secondary">
      {children}
    </Typography>
  </Box>
);

const Cell = ({ children }: { readonly children: ReactNode }) => (
  <Box pl={Spacing.XS} pb={Spacing.M} pt={Spacing.XS}>
    {children}
  </Box>
);

export const MortgageComparisonTable: React.FC<Props> = ({ originalLoanDuration, loanPaymentAmount, loanAmount, loanInterestRate }) => {
  const { t } = useResource();

  const loanTotalAmount = loanPaymentAmount * (originalLoanDuration * 12);

  const earlyRepaymentLoanDuration = originalLoanDuration - 5;
  const earlyRepaymentTotalAmount = loanPaymentAmount * (earlyRepaymentLoanDuration * 12);

  const rows: Variant[] = [
    {
      variant: t(Resources.AnalysisDetail.Goals.MorgageComparisonTable.OriginalRepayment),
      loanDuration: originalLoanDuration,
      loanTotalAmount,
      savings: 0,
      oneTimeAmount: 0,
    },
    {
      variant: t(Resources.AnalysisDetail.Goals.MorgageComparisonTable.EarlyRepayment),
      loanDuration: earlyRepaymentLoanDuration,
      loanTotalAmount: earlyRepaymentTotalAmount,
      savings: loanTotalAmount - earlyRepaymentTotalAmount,
      oneTimeAmount: calculateFinalOneTimeRepaymentAmount({
        durationInYears: earlyRepaymentLoanDuration,
        loanAmount,
        monthlyPaymentAmount: loanPaymentAmount,
        loanInterestRate,
      }),
    },
  ];

  return (
    <DataTable<Variant>
      size="small"
      rows={rows}
      getRowId={(row) => row.variant}
      columns={[
        {
          id: "variant",
          header: <HeaderCell>{t(Resources.AnalysisDetail.Goals.MorgageComparisonTable.Variant)}</HeaderCell>,
          renderCell: (row) => (
            <Cell>
              <Typography variant="caption" fontWeight={500}>
                {row.variant}
              </Typography>
            </Cell>
          ),
        },
        {
          id: "loanDuration",
          header: <HeaderCell>{t(Resources.AnalysisDetail.Goals.MorgageComparisonTable.LoanDuration)}</HeaderCell>,
          renderCell: (row) => (
            <Cell>
              <Typography variant="caption">
                {row.loanDuration} {t(Resources.Common.Years)}
              </Typography>
            </Cell>
          ),
        },
        {
          id: "loanTotalAmount",
          header: <HeaderCell>{t(Resources.AnalysisDetail.Goals.MorgageComparisonTable.TotalLoanAmount)}</HeaderCell>,
          renderCell: (row) => (
            <Cell>
              <Typography variant="caption">{formatCurrency(row.loanTotalAmount, CurrencyCode.CZK, 0)}</Typography>
            </Cell>
          ),
        },
        {
          id: "oneTimeAmount",
          header: <HeaderCell>{t(Resources.AnalysisDetail.Goals.MorgageComparisonTable.OneTimeAmount)}</HeaderCell>,
          renderCell: (row) => (
            <Cell>
              {row.oneTimeAmount > 0 ? (
                <Typography variant="caption">{formatCurrency(row.oneTimeAmount, CurrencyCode.CZK, 0)}</Typography>
              ) : (
                <Typography variant="caption">-</Typography>
              )}
            </Cell>
          ),
        },
        {
          id: "savings",
          header: <HeaderCell>{t(Resources.AnalysisDetail.Goals.MorgageComparisonTable.Savings)}</HeaderCell>,
          renderCell: (row) => (
            <Cell>
              {row.savings > 0 ? (
                <Typography color="success" variant="caption">
                  {formatCurrency(row.savings, CurrencyCode.CZK, 0)}
                </Typography>
              ) : (
                <Typography variant="caption">-</Typography>
              )}
            </Cell>
          ),
        },
      ]}
    />
  );
};
